import { WeightedTable } from "./WeightedTable";
import { InventoryManager } from "../inventory/InventoryManager";
import { Charm, CharmData, Charms } from "../inventory/Charms";
import { Item, ItemData, Items } from "../inventory/Items";
import { GameEvents } from "./Types";
import { Client } from "..";
import { OpponentLevel, StoreLevel, Level } from "./TimelineTypes";
import { BossPool } from "./BossData";
import { Phase } from "./StateMachine";
import { WheelSection } from "../wheel/WheelSection";

export class GameState {
  public timeline: Level[] = [];

  private _level: number = 1;
  public get level(): number {
    return this._level;
  }
  public set level(value: number) {
    if (value === this._level) return;
    this._level = value;
    Client.game.events.emit(GameEvents.LEVEL_UPDATED, this._level);
  }

  public get currentLevelData(): Level | undefined {
    return this.timeline[this.level - 1];
  }

  public currentLevelHistory: WheelSection[] = []; // Holds the history of scored section of the level
  public get lastScoredSection(): WheelSection | undefined {
    return this.currentLevelHistory[this.currentLevelHistory.length - 1];
  }

  private _timelineVisibility: number = 2; // How many levels ahead of the current level the player can see in the timeline.
  public get timelineVisibility(): number {
    return this._timelineVisibility;
  }
  public set timelineVisibility(value: number) {
    this._timelineVisibility = Math.max(0, value);
    if (value === this._timelineVisibility) return;
    Client.game.events.emit(
      GameEvents.TIMELINE_VISIBILITY_UPDATED,
      this._timelineVisibility,
    );
  }

  public maxNumberOfSpins: number = 5;
  private _availableSpins: number = 5;
  public get availableSpins(): number {
    return this._availableSpins;
  }
  public set availableSpins(value: number) {
    if (value === this._availableSpins) return;
    this._availableSpins = Math.max(value, 0);
    Client.game.events.emit(
      GameEvents.AVAILABLE_SPINS_UPDATED,
      this._availableSpins,
    );
  }

  private _wheelSpinning: boolean = false;
  public get wheelSpinning(): boolean {
    return this._wheelSpinning;
  }
  public set wheelSpinning(value: boolean) {
    if (value === this._wheelSpinning) return;
    this._wheelSpinning = value;
    Client.game.events.emit(
      GameEvents.WHEEL_SPINNING_STATE_CHANGED,
      this._wheelSpinning,
    );
  }

  private _phase: Phase = Phase.IDLE;
  public get phase(): Phase {
    return this._phase;
  }
  public set phase(value: Phase) {
    if (value === this._phase) return;
    this._phase = value;
    Client.game.events.emit(GameEvents.PHASE_UPDATED, this._phase);
  }

  private _win: number = 0;
  public get win(): number {
    return this._win;
  }
  public set win(value: number) {
    if (value === this._win) return;
    this._win = value;
    Client.game.events.emit(GameEvents.WIN_UPDATED, this._win);
  }

  private _winMultiplier: number = 1;
  public get winMultiplier(): number {
    return this._winMultiplier;
  }
  public set winMultiplier(value: number) {
    if (value === this._winMultiplier) return;
    this._winMultiplier = value;
    Client.game.events.emit(
      GameEvents.WIN_MULTIPLIER_UPDATED,
      this._winMultiplier,
      this._winMultiplierColor,
    );
  }

  private _winMultiplierColor: number = 0xc69c5a; // Default color
  public get winMultiplierColor(): number {
    return this._winMultiplierColor;
  }
  public set winMultiplierColor(value: number) {
    if (value === this._winMultiplierColor) return;
    this._winMultiplierColor = value;
    // Emit the updated event with both multiplier and color
    Client.game.events.emit(
      GameEvents.WIN_MULTIPLIER_UPDATED,
      this._winMultiplier,
      this._winMultiplierColor,
    );
  }

  private _score: number = 0;
  public get score(): number {
    return this._score;
  }
  public set score(value: number) {
    if (value === this._score) return;
    this._score = value;
    Client.game.events.emit(GameEvents.SCORE_UPDATED, this._score);
  }

  private _money: number = 0;
  public get money(): number {
    return this._money;
  }
  public set money(value: number) {
    if (value === this._money) return;
    this._money = value;
    Client.game.events.emit(GameEvents.MONEY_UPDATED, this._money);
  }

  public itemInventory: InventoryManager<Item, Items> = new InventoryManager<
    Item,
    Items
  >([], 4, GameEvents.ITEM_INVENTORY_UPDATED, ItemData);
  public charmInventory: InventoryManager<Charm, Charms> = new InventoryManager<
    Charm,
    Charms
  >([], 3, GameEvents.CHARM_INVENTORY_UPDATED, CharmData);

  public itemPool: WeightedTable<Item> = new WeightedTable<Item>();
  public charmPool: WeightedTable<Charm> = new WeightedTable<Charm>();

  public Serialize(): string {
    // TO DO: Improve serialization
    return JSON.stringify({
      score: this.score,
      level: this.level,
      timeline: this.timeline,
      timelineVisibility: this.timelineVisibility,
      itemInventory: this.itemInventory,
      charmInventory: this.charmInventory,
      itemPool: this.itemPool,
      charmPool: this.charmPool,
    });
  }

  public Deserialitze(data: string): void {
    // TO DO: Improve deserialization
    const obj = JSON.parse(data);
    this.score = obj.score;
    this.level = obj.level;
    this.timeline = obj.timeline;
    this.itemInventory = obj.itemInventory;
    this.charmInventory = obj.charmInventory;
    this.itemPool = obj.itemPool;
    this.charmPool = obj.charmPool;
  }

  /**
   * Generates a timeline with the pattern of 5 cycles: 2x generic opponent levels → 1x boss level → 1x shop level (no shop after final final boss)
   * Bosses are selected randomly from predefined pools organized by difficulty level
   */
  public generateTimeline(): void {
    this.timeline = [];

    // Accounts for: starting wheel (~150-200 points/5 spins), item progression, money rewards
    const optimalScores = [
      80,
      120,
      180,
      250,
      350,
      450,
      600,
      800,
      1000,
      1300,
      1600,
      1900,
      2300,
      2800,
      3500,
    ];

    const generateOpponent = function (combatLevelIndex: number): OpponentLevel {
      const opponent = new OpponentLevel();
      opponent.name = `Opponent ${combatLevelIndex + 1}`;
      opponent.description = `Face off against opponent ${combatLevelIndex + 1}`;
      opponent.requiredScore = optimalScores[combatLevelIndex] || (combatLevelIndex + 1) * 100; // Fallback to old system if beyond array
      return opponent;
    };

    let combatLevelIndex = 0; // Track actual combat levels (excluding shops)

    for (let cycle = 0; cycle < 5; cycle++) {
      // Add 2 opponent levels
      for (let opponentIndex = 0; opponentIndex < 2; opponentIndex++) {
        const opponent = generateOpponent(combatLevelIndex);
        this.timeline.push(opponent);
        combatLevelIndex++;
      }

      // Next add 1 boss level using the boss pool
      const difficultyLevel = cycle + 1; // Difficulty increases with each cycle (1-5)
      const boss = BossPool.getInstance().getRandomBoss(difficultyLevel);

      if (boss) {
        // Update boss score to match optimal curve
        boss.requiredScore = optimalScores[combatLevelIndex];
        this.timeline.push(boss);
        combatLevelIndex++;
      } else {
        // Fallback to generic opponent if pool is empty - should never happen in release anyways
        console.warn(
          `No boss available for difficulty level ${difficultyLevel}, creating generic opponent`,
        );
        const opponent = generateOpponent(combatLevelIndex);
        this.timeline.push(opponent);
        combatLevelIndex++;
      }

      // Add shop level after boss, except for the final boss (cycle 4 = boss 5)
      if (cycle < 4) {
        const shop = new StoreLevel();
        shop.name = `Shop`;
        shop.description = `Visit the shop to\nupgrade your equipment`;
        this.timeline.push(shop);
        // Note: shops don't increment combatLevelIndex since they're not combat levels
      }
    }
  }
}
